import React, { useEffect, useMemo, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import "../../sass/ui/modal.scss";

const Ctx = React.createContext({ open: false, onOpenChange: () => {} });

export const Dialog = ({ open, onOpenChange, children }) => {
  const value = useMemo(() => ({ open, onOpenChange }), [open, onOpenChange]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const DialogContent = ({
  className = "",
  children,
  maxWidth = "900px",
  closeOnOverlay = true,
}) => {
  const { open, onOpenChange } = useContext(Ctx);
  const contentRef = useRef(null);


  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onOpenChange?.(false);
      if (e.key === "Tab") {

        const root = contentRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const root = contentRef.current;
    if (!root) return;
    const focusables = root.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0];
    if (first && typeof first.focus === "function") {

      setTimeout(() => first.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay"
      onClick={closeOnOverlay ? () => onOpenChange?.(false) : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={contentRef}
        className={"modal-content " + className}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export const DialogHeader = ({ className = "", children }) => (
  <div className={"modal-header " + className}>{children}</div>
);

export const DialogTitle = ({ className = "", children }) => (
  <h2 className={"modal-title " + className}>{children}</h2>
);

export const DialogFooter = ({ className = "", children }) => (
  <div className={"modal-footer " + className}>{children}</div>
);

export default Dialog;
